import { useABAC } from '@adornis/abac/client/useABAC.js';
import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { LMSAction } from '@nsc/simple-lms/init.js';
import { getMyColleagueAccess } from '../../api/colleague-ticket-queries.js';
import { accessController } from '../../features/permissions/init-permissions.js';
import * as m from '../../localization/out/messages.js';
import { SmartTeamsAppPermission } from '../../features/permissions/roles-and-permissions.js';
import { router } from '../startup.js';

export const AppNavbar = stateful(() => {
  const currentRoute = router.currentRoute.value.route.path;
  const [toast, setToast] = useState<string | null>(null);

  const { can: isAdmin } = useABAC(accessController, createPermissionPayload(SmartTeamsAppPermission.UserManage));
  const { can: canViewCourse } = useABAC(accessController, createPermissionPayload(LMSAction.ViewCourse));
  // A partner engineer sees only their own tickets, so they get no Colleagues tab (the server enforces it too).
  const colleagueAccessQuery = useQuery(() => getMyColleagueAccess()(), []);

  const showComingSoon = (label: string) => {
    setToast(m.nav_comingSoon({ label }));
    setTimeout(() => setToast(null), 2000);
  };

  const tab = (path: string, icon: unknown, label: string, opts?: { disabled?: boolean; onClick?: () => void }) => {
    const isActive = path === '/' ? currentRoute === '/' : currentRoute.startsWith(path);
    const disabled = opts?.disabled ?? false;
    return html`
      <button
        class="flex flex-col items-center justify-center flex-1 py-2 gap-0.5 transition-colors
          ${isActive ? 'text-primary border-t-2 border-primary' : 'text-base-content/40 border-t-2 border-transparent'}
          ${disabled ? 'opacity-30' : 'hover:text-primary/70'}"
        @click=${() => (disabled ? showComingSoon(label) : opts?.onClick?.())}
      >
        ${icon}
        <span class="text-[10px] leading-tight font-medium">${label}</span>
      </button>
    `;
  };

  return html`
    <nav class="flex bg-base-100 border-t border-base-content/20 shrink-0 relative">
      ${toast
        ? html`
            <div
              class="absolute -top-12 left-1/2 -translate-x-1/2 bg-base-content text-base-100 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg whitespace-nowrap"
            >
              ${toast}
            </div>
          `
        : nothing}
      <div class="flex-1 flex">
        ${tab(
          '/',
          html`<span class="icon-[material-symbols--confirmation-number-outline] text-[22px]"></span>`,
          m.nav_tickets(),
          {
            onClick: () => router.goTo('tickets'),
          },
        )}
        ${colleagueAccessQuery.value === true
          ? tab(
              '/colleagues',
              html`<span class="icon-[material-symbols--groups-outline] text-[22px]"></span>`,
              m.nav_colleagues(),
              {
                onClick: () => router.goTo('colleagueTickets'),
              },
            )
          : nothing}
        ${canViewCourse
          ? tab(
              '/academy',
              html`<span class="icon-[material-symbols--school-outline] text-[22px]"></span>`,
              m.nav_academy(),
              {
                onClick: () => router.goTo('academy'),
              },
            )
          : nothing}
        ${isAdmin
          ? tab(
              '/users',
              html`<span class="icon-[material-symbols--group-outline] text-[22px]"></span>`,
              m.nav_users(),
              {
                onClick: () => router.goTo('users'),
              },
            )
          : nothing}
        ${tab(
          '/profile',
          html`<span class="icon-[material-symbols--account-circle] text-[22px]"></span>`,
          m.nav_profile(),
          {
            onClick: () => router.goTo('profile'),
          },
        )}
      </div>
    </nav>
  `;
});
